import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { getEnvironmentalData, saveEnvironmentalForm } from '../../../../../../api/transitions';
import { enqueueSnackbar } from '../../../../../../reusable-components/snackbar';
import { trimData } from '../../../../../../utils/formatText';
import SkeletonBlock from '../../../SkeletonBlock';
import { flattenTransitionSubmitData } from '../../../formDataUtils';
import { ENVIRONMENTAL } from '../../../formQuestionsEnum';
import HousekeepingForm from './HousekeepingForm';
import LaundryForm from './LaundryForm';

const EnvironmentalView = forwardRef(({ setFormData }, ref) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const facilityId = searchParams.get('facilityId');
  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const userPermissions = useSelector((state) => state.transitions?.data?.userPermissions);

  const environmentalPermissions = Object.fromEntries(
    Object.values(userPermissions?.[id]?.[facilityId]?.environmentalFieldPermissions || {}).map((x) => [
      x.formQuestionId,
      x.isEditable,
    ])
  );

  useEffect(() => {
    getEnvironmentalData(id, facilityId)
      .then((resp) => {
        setData(resp.data);
      })
      .catch(() => {
        enqueueSnackbar('Failed to load data', { variant: 'error' });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id, facilityId]);

  useEffect(() => {
    const fieldNameMap = Object.fromEntries(Object.entries(ENVIRONMENTAL).map(([, v]) => [v.id, v.fieldName]));

    const mappedFields = {
      ...data?.fields?.reduce((acc, field) => ({ ...acc, [fieldNameMap[field.formQuestionId]]: field.answer }), {}),
    };

    setFormData({ transitionFacilityId: data?.transitionFacilityId, ...mappedFields });
  }, [setFormData, data]);

  const submitForm = async (allData) => {
    const flattenData = flattenTransitionSubmitData(allData);
    const result = trimData(flattenData);

    await saveEnvironmentalForm({
      id,
      sellerFacilityId: facilityId,
      ...result,
    })
      .then((resp) => {
        enqueueSnackbar('Environmental data saved successfully', { variant: 'success' });
        setData(resp.data);
      })
      .catch(() => {
        enqueueSnackbar('Failed to save Environmental data', { variant: 'error' });
      });
  };

  useImperativeHandle(ref, () => ({ submitForm }));

  return isLoading ? (
    <SkeletonBlock height={600} sx={{ mt: 2 }} />
  ) : (
    <>
      <HousekeepingForm permissions={environmentalPermissions} />
      <LaundryForm permissions={environmentalPermissions} />
    </>
  );
});

export default EnvironmentalView;

EnvironmentalView.propTypes = {
  setFormData: PropTypes.func,
};
